import { Box, Typography, useTheme } from "@mui/material";
import type { Pitch } from "@/modules/zoom-calls/types";
import {
  asTrimmedString,
  formatLabel,
} from "@/modules/zoom-calls/components/analysis.helpers";
import { reactionStyle } from "./extracted-fields.helpers";

const ClientReactionRow = ({ pitch }: { pitch: Pitch }) => {
  const theme = useTheme();
  const p = theme.nawy.colors.primitives;
  const indicator = theme.nawy.colors.semantic.indicator;

  const reaction = asTrimmedString(pitch.client_reaction);
  const evidence = asTrimmedString(pitch.client_reaction_evidence);
  const objection = pitch.objection;
  const objectionType = asTrimmedString(objection?.type);
  const objectionQuote = asTrimmedString(objection?.quote);

  if (!reaction && !evidence && !objectionType && !objectionQuote) return null;

  const { Icon, colorToken, label } = reactionStyle(reaction);
  const color = indicator[colorToken];
  // For `requested_info` the label already reads as a standalone phrase ("Client
  // request"); every other reaction is prefixed with "Client reaction:".
  const headline =
    reaction?.toLowerCase() === "requested_info"
      ? label
      : `Client reaction: ${label}`;

  const quoteSx = {
    fontSize: 12,
    color: p.grey[700],
    fontStyle: "italic" as const,
    textAlign: "right" as const,
    wordBreak: "break-word" as const,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        // Own white rounded box on the grey requirement card (per Figma).
        borderRadius: "10px",
        backgroundColor: theme.nawy.colors.semantic.layer.primary,
        p: "12px",
      }}
    >
      {reaction && (
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Icon size={14} color={color} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color }}>
            {headline}
          </Typography>
        </Box>
      )}

      {evidence && (
        <Typography dir="rtl" sx={quoteSx}>
          {evidence}
        </Typography>
      )}

      {(objectionType || (objectionQuote && objectionQuote !== evidence)) && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            mt: "2px",
          }}
        >
          {objectionType && (
            <Typography
              sx={{ fontSize: 11, fontWeight: 600, color: indicator.red }}
            >
              {`Objection: ${formatLabel(objectionType)}`}
            </Typography>
          )}
          {objectionQuote && objectionQuote !== evidence && (
            <Typography dir="rtl" sx={quoteSx}>
              {objectionQuote}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ClientReactionRow;
